import React, { Component } from 'react';
import { Route } from 'react-router-dom';
import SectionImg from "../SectionImg";
import Month from "./Month";
import Admin from "./Admin";

import StorageInterface from "../../api scripts/storageInterface";
import CalendarInterface from "../../api scripts/calendarInterface";
import { isNull } from '../../api scripts/utils';


class Calendar extends Component {
  constructor(props) {
    super(props)
    this.state = {
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
      allCalendarData: {},
      currentMonthData: null,
      isLoadingMonth: false,
      isFetchingFromS3: false,
      fetchNotification: null
    }

    this.changeMonth = this.changeMonth.bind(this);
  }

  async componentDidMount() {
    this.setCurrentDateToState();
    
    // Show calendar immediately with local data for better UX
    const { viewYear, viewMonth } = this.state;
    const localMonthData = this.makeCalendarData(viewYear, viewMonth);
    this.setCalendarDataToState(localMonthData);
    this.setState({ currentMonthData: localMonthData[0] });
    
    // Fetch S3 data in the background and update if available
    // This allows the calendar to render immediately while data loads
    this.loadCalendarDataInBackground();
  }

  async loadCalendarDataInBackground() {
    this.setState({ 
      isFetchingFromS3: true,
      fetchNotification: 'Searching for scheduled classes...'
    });
    
    try {
      const calendarData = await this.getCalendarData();
      if (calendarData && Array.isArray(calendarData) && calendarData.length > 0) {
        // Store all fetched months in state
        this.setCalendarDataToState(calendarData);
        
        // Update current month with S3 data if available
        const { viewYear, viewMonth } = this.state;
        const monthData = calendarData.find(monthEntry => 
          monthEntry.year === viewYear && monthEntry.monthNo === viewMonth
        );
        
        if (monthData) {
          // Update current month data with S3 data
          this.setState({ 
            currentMonthData: monthData,
            isFetchingFromS3: false,
            fetchNotification: 'Classes loaded successfully!'
          });
        } else {
          // S3 data loaded but no data for current month - keep local data
          this.setState({ 
            isFetchingFromS3: false,
            fetchNotification: null
          });
        }
        
        // Clear success notification after 2 seconds
        if (monthData) {
          setTimeout(() => {
            this.setState({ fetchNotification: null });
          }, 2000);
        }
      } else {
        this.setState({ 
          isFetchingFromS3: false,
          fetchNotification: null
        });
      }
    } catch (error) {
      console.error('Error loading calendar data in background:', error);
      this.setState({ 
        isFetchingFromS3: false,
        fetchNotification: 'Using local calendar data'
      });
      // Clear error notification after 3 seconds
      setTimeout(() => {
        this.setState({ fetchNotification: null });
      }, 3000);
    }
  }

  async getCalendarData() {
    try {
      let dl = await StorageInterface.downloadData(); // Auto-detect environment
      // Check if it's an error message (has line1 property)
      if (dl && dl.line1) {
        console.error('[Calendar] Error fetching data:', dl);
        return null;
      }
      // StorageInterface already extracts the data array, so dl is the array directly
      if (dl && Array.isArray(dl) && dl.length > 0) {
        return dl;
      }
    } catch (error) {
      console.error('Error downloading calendar data:', error);
    }
    // Return null instead of generating data here - let callers handle fallback
    return null;
  }

  async getCalendarDataForMonth(year, month, forceFetch = false) {
    // Check if we already have this month in state first (avoid redundant API calls)
    // Skip cache check if forceFetch is true
    if (!forceFetch) {
      const { allCalendarData } = this.state;
      if (allCalendarData[year] && allCalendarData[year][month]) {
        return [allCalendarData[year][month]];
      }
    }
    
    // Try to get data from S3
    try {
      let dl = await StorageInterface.downloadData(); // Auto-detect environment
      
      // Check if dl is the error message object
      if (dl && dl.line1) {
        console.error('[Calendar] Error fetching data:', dl);
        throw new Error(dl.line1);
      }
      
      // Handle the response - dl is now directly the array of calendar data
      if (dl && Array.isArray(dl)) {
        // Store all fetched months in state for future use
        this.setCalendarDataToState(dl);
        
        // Filter the downloaded data to find the specific month
        const monthData = dl.find(monthEntry => 
          monthEntry.year === year && monthEntry.monthNo === month
        );
        
        if (monthData) {
          return [monthData];
        }
      }
    } catch (error) {
      console.error('Error fetching calendar data from S3:', error);
    }
    
    // If no data found in S3 for this month, generate local data
    if (process.env.NODE_ENV !== 'production') {
      console.log(`No S3 data found for ${year}/${month}, generating local data`);
    }
    return this.makeCalendarData(year, month);
  }

  makeCalendarData(year = null, month = null) {
    const viewYear = year || new Date().getFullYear();
    const viewMonth = month !== null ? month : new Date().getMonth();
    console.log("making the calendarData for: ", viewYear, viewMonth);
    return [
      new CalendarInterface(viewYear, viewMonth)
        .exportMonthData()
    ];
  }

  copyStateCalendarData() {
    const { allCalendarData } = this.state;
    const stateCalendarDataCopy = {};
    
    for (let year in allCalendarData) {
      stateCalendarDataCopy[year] = {};
      for (let month in allCalendarData[year]) {
        const calendarInterface = new CalendarInterface();
        const monthCopy = calendarInterface.copyExternalMonthData(allCalendarData[year][month]);
        stateCalendarDataCopy[year][month] = monthCopy;
      }
    }
    return stateCalendarDataCopy;
  }

  setCalendarDataToState(calendarData) {
    this.setState((state) => {
      const stateCalendarData = { ...state.allCalendarData };

      if (Array.isArray(calendarData)) {
        calendarData.forEach(month => {
          const { year, monthNo } = month;
          if (!stateCalendarData[year]) stateCalendarData[year] = {};
          stateCalendarData[year][monthNo] = month;
        });
      } else {
        // Handle single month data
        const { year, monthNo } = calendarData;
        if (!stateCalendarData[year]) stateCalendarData[year] = {};
        stateCalendarData[year][monthNo] = calendarData;
      }

      return { allCalendarData: stateCalendarData };
    });
  }

  setCurrentDateToState() {
    this.setState({
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth()
    });
  }

  getMonthName(monthIndex) {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthIndex];
  }

  async changeMonth(change) {
    const { viewMonth, viewYear } = this.state;
    const { newMonth, newYear } = this.determineNewMonthYear(change);
    
    // Check if we already have this month's data cached
    const hasCachedData = this.isMonthYearAvailable({ newMonth, newYear });
    
    // Set loading state only if we need to fetch data
    if (!hasCachedData) {
      this.setState({ isLoadingMonth: true });
    }

    try {
      // If we don't have cached data, try to get it (but show local data immediately)
      if (!hasCachedData) {
        // Show local calendar immediately for better UX
        const localData = this.makeCalendarData(newYear, newMonth);
        this.setCalendarDataToState(localData);
        
        // Update view state immediately
        this.setState({
          viewYear: newYear,
          viewMonth: newMonth,
          currentMonthData: localData[0],
          isLoadingMonth: false
        });
        
        // Fetch from S3 in background and update if better data is available
        // Pass forceFetch=true to skip the cache check we just populated with local data
        this.setState({ 
          isFetchingFromS3: true,
          fetchNotification: 'Searching for scheduled classes...'
        });
        
        this.getCalendarDataForMonth(newYear, newMonth, true).then(newCalendarData => {
          if (newCalendarData && newCalendarData[0]) {
            // Check if this is actually different from local data (has events or is from S3)
            const hasEvents = newCalendarData[0].days.some(day => day.events.length > 0 || day.holiday);
            const currentData = this.state.allCalendarData[newYear]?.[newMonth];
            const currentHasEvents = currentData?.days.some(day => day.events.length > 0 || day.holiday);
            
            // Only update if new data has more information than current data
            if (hasEvents || !currentHasEvents) {
              this.setCalendarDataToState(newCalendarData);
              // Only update if we're still viewing this month
              if (this.state.viewYear === newYear && this.state.viewMonth === newMonth) {
                this.setState({ 
                  currentMonthData: newCalendarData[0],
                  isFetchingFromS3: false,
                  fetchNotification: hasEvents ? 'Classes loaded!' : null
                });
                // Clear success notification after 2 seconds
                if (hasEvents) {
                  setTimeout(() => {
                    this.setState({ fetchNotification: null });
                  }, 2000);
                }
              } else {
                this.setState({ isFetchingFromS3: false, fetchNotification: null });
              }
            } else {
              this.setState({ isFetchingFromS3: false, fetchNotification: null });
            }
          } else {
            this.setState({ isFetchingFromS3: false, fetchNotification: null });
          }
        }).catch(error => {
          console.error('Error fetching month data in background:', error);
          this.setState({ 
            isFetchingFromS3: false,
            fetchNotification: 'Using local calendar data'
          });
          setTimeout(() => {
            this.setState({ fetchNotification: null });
          }, 3000);
        });
      } else {
        // We have cached data, just update the view
        this.setState({
          viewYear: newYear,
          viewMonth: newMonth
        }, async () => {
          await this.loadCurrentMonthData();
        });
      }
    } catch (error) {
      console.error('Error changing month:', error);
      // Fallback to local data if anything fails
      const fallbackData = this.makeCalendarData(newYear, newMonth);
      this.setCalendarDataToState(fallbackData);
      this.setState({
        viewYear: newYear,
        viewMonth: newMonth,
        currentMonthData: fallbackData[0],
        isLoadingMonth: false
      });
    }
  }

  determineNewMonthYear(change) {
    const { viewMonth, viewYear } = this.state;
    console.log(`determineNewMonthYear called - \n${this.getMonthName(viewMonth)} ${viewYear} (month index: ${viewMonth})`);
    let newMonth;
    let newYear;

    switch (change) {
      case 1:
        console.log('Next month button clicked');
        if (viewMonth < 11) { // JavaScript months are 0-11
          newMonth = viewMonth + 1;
          newYear = viewYear;
          console.log(`Moving to next month in same year: \n${this.getMonthName(newMonth)} ${newYear} (month index: ${newMonth})`);
        } else {
          newMonth = 0; // January is 0
          newYear = viewYear + 1;
          console.log(`Moving to January of next year: ${newYear}`);
        }
        break;
      case -1:
        console.log('Previous month button clicked');
        if (viewMonth > 0) { // JavaScript months are 0-11
          newMonth = viewMonth - 1;
          newYear = viewYear;
          console.log(`Moving to previous month in same year: ${newMonth}`);
        } else {
          newMonth = 11; // December is 11
          newYear = viewYear - 1;
          console.log(`Moving to December of previous year: ${newYear}`);
        }
        break;
      default:
        console.log("default case - no change");
        newMonth = viewMonth;
        newYear = viewYear;
        break;
    }

    console.log(`Returning - \n${this.getMonthName(newMonth)} ${newYear} (month index: ${newMonth})`);
    return { newMonth, newYear };
  }

  isMonthYearAvailable({ newMonth: month, newYear: year }) {
    const { allCalendarData } = this.state;
    return (
      allCalendarData[year] !== undefined && 
      allCalendarData[year][month] !== undefined
    );
  }

  fillExtraSpacesOnCalendar(monthData) {
    let buffer = { 
      "dayOfWeek": null,
      "date": null,
      "holiday": false,
      "message": null,
      "events": [] 
    };
    
    let bufferCount = monthData.days[0].dayOfWeek;
    let bufferedMonth = monthData.days.slice();

    for (let i = 0; i < bufferCount; i++){
      bufferedMonth.unshift(buffer);
    }

    while (bufferedMonth.length % 7 !== 0) {
      bufferedMonth.push(buffer);
    }

    return bufferedMonth;
  }

  breakMonthIntoWeeks(bufferedMonth) {
    let monthInWeeks = [];

    for (let i = 0; i < bufferedMonth.length; i += 7) {
      monthInWeeks.push(bufferedMonth.slice(i, i + 7))
    }

    return monthInWeeks;
  }

  getMonthInWeeks(monthData) {
    const bufferedMonth = this.fillExtraSpacesOnCalendar(monthData);
    const monthInWeeks = this.breakMonthIntoWeeks(bufferedMonth);
    return monthInWeeks;
  }

  async getMonthData() {
    const { allCalendarData, viewMonth, viewYear } = this.state;
    
    // First check if we have cached data
    if (allCalendarData[viewYear] !== undefined && 
        allCalendarData[viewYear][viewMonth] !== undefined) {
      return allCalendarData[viewYear][viewMonth];
    }
    
    // If not cached, generate local data immediately (fast)
    const localData = this.makeCalendarData(viewYear, viewMonth);
    this.setCalendarDataToState(localData);
    
    // Try to fetch from S3 in background (non-blocking)
    // Pass forceFetch=true to skip the cache check we just populated with local data
    // This will update the data if S3 has better information
    this.getCalendarDataForMonth(viewYear, viewMonth, true).catch(error => {
      console.error('Error fetching calendar data:', error);
    });
    
    // Return local data immediately
    return localData[0];
  }

  setMonthData(monthData) {
    const { allCalendarData, viewMonth, viewYear } = this.state;
    if (!isNull(allCalendarData) && !isNull(viewMonth) && !isNull(viewYear)) {
      const updatedData = { ...allCalendarData };
      if (!updatedData[viewYear]) updatedData[viewYear] = {};
      updatedData[viewYear][viewMonth] = monthData;
      this.setState({ allCalendarData: updatedData });
    }
  }

  addClassHandler = (date) => {
    const { currentMonthData } = this.state;
    if (currentMonthData) {
      console.log(`Add a class to ${JSON.stringify(currentMonthData.days[date -1])}`);
    }
  }

  removeClassHandler = async (eventNo, date) => {
    const { currentMonthData } = this.state;
    if (!currentMonthData) return;
    
    const calendarInterface = new CalendarInterface();
    const month = calendarInterface.importMonthData(currentMonthData);
    month.removeEvent(date -1, eventNo);
    const updatedMonthData = month.exportMonthData();
    
    // Update both the calendar data and current month data
    this.setMonthData(updatedMonthData);
    this.setState({ currentMonthData: updatedMonthData });
    
    // TODO - Update the UI when an event is removed 
    console.log(">>>>>>>>> events "+ JSON.stringify(month.getEventsForDay(date -1)));
    console.log(`Remove class ${eventNo} from ${currentMonthData.monthName} the ${date}`);
  }

  updateClassHandler = (eventNo, date) => {
    const { currentMonthData } = this.state;
    if (currentMonthData) {
      console.log(`Update class ${eventNo} from ${currentMonthData.monthName} the ${date}`);
    }
  }

  adminFuncs = {
    addClass: this.addClassHandler,
    removeClass: this.removeClassHandler,
    updateClass: this.updateClassHandler
  }

  async loadCurrentMonthData() {
    const { viewYear, viewMonth } = this.state;
    // Capture the intended year/month to prevent race conditions
    const targetYear = viewYear;
    const targetMonth = viewMonth;
    
    console.log(`loadCurrentMonthData - showing values \n${this.getMonthName(viewMonth)} ${viewYear} (month index: ${viewMonth})`);
    this.setState({ isLoadingMonth: true });
    
    try {
      const monthData = await this.getMonthData();
      console.log(`loadCurrentMonthData monthData ${monthData.monthName} ${monthData.year}`);
      
      // Only update data if we're still viewing the same month (prevent race conditions)
      if (this.state.viewYear === targetYear && this.state.viewMonth === targetMonth) {
        this.setState({ 
          currentMonthData: monthData,
          isLoadingMonth: false 
        });
      } else {
        console.log(`Ignoring stale data for ${this.getMonthName(targetMonth)} ${targetYear} - now viewing ${this.getMonthName(this.state.viewMonth)} ${this.state.viewYear}`);
        // Still need to reset loading state even if data is stale
        this.setState({ isLoadingMonth: false });
      }
    } catch (error) {
      console.error('Error loading month data:', error);
      // Always reset loading state on error, regardless of current month
      this.setState({ isLoadingMonth: false });
    }
  }

  render() {
    let { history, match } = this.props;
    const { allCalendarData, viewMonth, viewYear, currentMonthData, isLoadingMonth, isFetchingFromS3, fetchNotification } = this.state;
    const isAdmin = match.path === "/calendar/admin";
    console.log('in calendar', allCalendarData);

    let monthInWeeks;
    if (currentMonthData && !isLoadingMonth) {
      monthInWeeks = this.getMonthInWeeks(currentMonthData);
    }
  
    return (
      <div id="calendarContainer">
        <SectionImg title="Class calendar" pageName="Calendar" imgClass="defaultSectionImg" />
        
        {/* Notification banner for S3 fetch status */}
        {fetchNotification && (
          <div className={`calendar-notification ${isFetchingFromS3 ? 'fetching' : 'success'}`}>
            {isFetchingFromS3 && <span className="notification-spinner">⟳</span>}
            <span>{fetchNotification}</span>
          </div>
        )}
        
        {/* Create popup menu to update classes from clicks on single day
         < Route 
          exact path="/calendar/admin" 
          render={
            () => <Admin 
                    allCalendarData={this.state.allCalendarData}
                    viewMonth={this.state.viewMonth} 
                    viewYear={this.state.viewYear} 
                    monthData={currentMonthData} />
          } /> */}
        {
          isLoadingMonth ? 
            <div className="calendar-loading">
              <p>Loading calendar...</p>
            </div>
          : (monthInWeeks !== undefined && currentMonthData) ?
            <Month 
              adminFuncs={this.adminFuncs}
              allCalendarData={allCalendarData}
              monthInWeeks={monthInWeeks}
              changeMonth={this.changeMonth}
              monthData={currentMonthData}
              nextMonthAvailable={true}
              previousMonthAvailable={true}
              history={history}
              match={match}
              />
            :
            <div className="calendar-loading">
              <p>Loading calendar...</p>
            </div>
        }
      </div>
    )
  }
}

export default Calendar;